import { FC, useState } from 'react';
import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Menu from '@/components/menu';
import Text from '@/components/text';
import AccordionButton from '@/components/accordion-button';
import { FilterHeaderColProps, getFieldValue, hasFieldValues } from './helpers';
import FilterField from './filter-field';
import { DtFilterField } from '../helpers';
import FilterHeaderActionButton from './action-button';

interface Props extends FilterHeaderColProps {
  fields: DtFilterField[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  // To help with menu height in support of the long violations list, we will set a max height and allow for scrolling.
  content: {
    width: 360,
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
  },
  section: {
    '& .MuiAccordion-root': {
      borderRadius: theme.shape.borderRadius,
    },
  },
  sectionActive: {
    '& .MuiAccordion-root': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
}));

const MoreFiltersContent: FC<Props> = ({
  fields,
  values,
  setValueForColumn,
  setColumnToDefault,
}) => {
  const styles = useStyles();
  const [openedFields] = useState(fields);
  const hasFields = !!openedFields.length;

  if (!hasFields) {
    return (
      <Stack className={styles.content} p={1}>
        <Text size="small" color="grey">
          All filters have been added.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack className={styles.content} gap={1.5}>
      {openedFields.map((field) => {
        const { columnName, label, control = 'select', options = [] } = field;
        const currentValue = getFieldValue(values, columnName);
        const isActive = hasFieldValues(control, currentValue, options);
        return (
          <Stack key={columnName} className={styles.section} gap={1}>
            <AccordionButton
              title={
                <Text size="small" bold>
                  {label || columnName}
                </Text>
              }
              defaultOpen={isActive}
              className={isActive ? styles.sectionActive : undefined}
            >
              <FilterField
                field={field}
                values={values}
                setValueForColumn={setValueForColumn}
                setColumnToDefault={setColumnToDefault}
              />
            </AccordionButton>
          </Stack>
        );
      })}
    </Stack>
  );
};

const MoreFiltersMenu: FC<Props> = ({ fields, values, setValueForColumn, setColumnToDefault }) => {
  const inactiveFields = fields.filter((field) => {
    const { columnName, control = 'select', options = [] } = field;
    return !hasFieldValues(control, getFieldValue(values, columnName), options);
  });

  return (
    <Menu
      menuId="filter-header-more-filters"
      title=""
      trigger={<FilterHeaderActionButton icon="plus" label="More Filters" />}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <MoreFiltersContent
        fields={inactiveFields}
        values={values}
        setValueForColumn={setValueForColumn}
        setColumnToDefault={setColumnToDefault}
      />
    </Menu>
  );
};

export default MoreFiltersMenu;
